import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtEUR, customerSearchMode } from "@/lib/api";
import { toast } from "sonner";
import BarcodeScanner from "@/components/BarcodeScanner";
import DisplayPanel from "@/components/pos/DisplayPanel";
import CustomerSearch from "@/components/pos/CustomerSearch";
import CartTable from "@/components/pos/CartTable";
import ItemGrid from "@/components/pos/ItemGrid";
import PosActionBar from "@/components/pos/PosActionBar";
import ParkedSalesDialog from "@/components/pos/ParkedSalesDialog";
import ReturnDialog from "@/components/pos/ReturnDialog";
import { Warning, ArrowRight } from "@phosphor-icons/react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { computeTotals } from "@/lib/cart-utils";
import { useNumpad } from "@/lib/numpad-context";
import { useAuth } from "@/lib/auth-context";
import { useSuccessOverlay } from "@/lib/success-overlay";

const READY = {
    status: "ready",
    title: "ΑΝΑΜΟΝΗ ΠΕΛΑΤΗ",
    primary: "ΑΝΑΖΗΤΗΣΤΕ ΠΕΛΑΤΗ",
    lines: ["Πληκτρολογήστε κωδικό / ΑΦΜ ή σαρώστε QR/barcode"],
};

const GR_MONTHS = [
    "ΙΑΝ",
    "ΦΕΒ",
    "ΜΑΡ",
    "ΑΠΡ",
    "ΜΑΪ",
    "ΙΟΥΝ",
    "ΙΟΥΛ",
    "ΑΥΓ",
    "ΣΕΠ",
    "ΟΚΤ",
    "ΝΟΕ",
    "ΔΕΚ",
];
const monthLabelGr = (key) => {
    if (!key || !/^\d{4}-\d{2}$/.test(key)) return key || "";
    const [y, m] = key.split("-");
    return `${GR_MONTHS[parseInt(m, 10) - 1] || m} ${y}`;
};

export default function Dashboard() {
    const navigate = useNavigate();
    const numpad = useNumpad();
    const { canAccessSecurity } = useAuth();
    const successOverlay = useSuccessOverlay();
    const [customer, setCustomer] = useState(null);
    const [code, setCode] = useState("");
    const [searching, setSearching] = useState(false);
    const [items, setItems] = useState([]);
    const [cart, setCart] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [display, setDisplay] = useState(READY);
    const [scannerOpen, setScannerOpen] = useState(false);
    const [scannerMode, setScannerMode] = useState("item");
    const [parkedOpen, setParkedOpen] = useState(false);
    const [returnOpen, setReturnOpen] = useState(false);
    const [parkedCount, setParkedCount] = useState(0);
    const [favourites, setFavourites] = useState([]);
    const [activeTarget, setActiveTarget] = useState("customer"); // 'customer' | 'item'
    const [itemFilter, setItemFilter] = useState("");
    const [activeCategory, setActiveCategory] = useState("ALL");
    const [confirmClearOpen, setConfirmClearOpen] = useState(false);
    const [periodLimit, setPeriodLimit] = useState(null); // {month_key, amount, is_set}
    const searchRef = useRef(null);
    const itemSearchRef = useRef(null);

    const focusItemSearch = () => {
        setTimeout(() => {
            itemSearchRef.current?.focus();
            setActiveTarget("item");
        }, 50);
    };

    const focusCustomerSearch = () => {
        setTimeout(() => {
            searchRef.current?.focus();
            setActiveTarget("customer");
        }, 50);
    };

    const loadItems = () =>
        api.get("/items").then(({ data }) => setItems(data)).catch(() => {});

    const loadParkedCount = () =>
        api
            .get("/sales/parked")
            .then(({ data }) => setParkedCount(data.length))
            .catch(() => setParkedCount(0));

    const loadPeriodLimit = () =>
        api
            .get("/monthly-limits/current")
            .then(({ data }) => setPeriodLimit(data))
            .catch(() => setPeriodLimit(null));

    useEffect(() => {
        loadItems();
        loadParkedCount();
        loadPeriodLimit();
        // Re-check periodically and on tab focus so a new month is detected
        const iv = setInterval(loadPeriodLimit, 60_000);
        const onFocus = () => loadPeriodLimit();
        window.addEventListener("focus", onFocus);
        return () => {
            clearInterval(iv);
            window.removeEventListener("focus", onFocus);
        };
    }, []);

    // Auto-clear customer after N seconds of inactivity (cart empty).
    // Countdown is exposed via `clearCountdown` so the DisplayPanel can show it.
    const [clearCountdown, setClearCountdown] = useState(null);
    const [clearTimeoutSec, setClearTimeoutSec] = useState(5);

    // Load configurable POS timeout from server settings (defaults to 5 sec).
    useEffect(() => {
        api.get("/pos-settings")
            .then((r) => {
                const t = Number(r.data?.customer_clear_timeout);
                if (t && t >= 2 && t <= 120) setClearTimeoutSec(t);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!customer) {
            setClearCountdown(null);
            return;
        }
        if (cart.length > 0) {
            setClearCountdown(null); // user started a sale → cancel timer
            return;
        }
        // Visible countdown ticks every 1s, N → 1
        setClearCountdown(clearTimeoutSec);
        const ticker = setInterval(() => {
            setClearCountdown((n) => (n && n > 1 ? n - 1 : n));
        }, 1000);
        const t = setTimeout(() => {
            setCustomer(null);
            setCart([]);
            setCode("");
            setDisplay(READY);
            setClearCountdown(null);
            successOverlay.showCustomerCleared();
            focusCustomerSearch();
        }, clearTimeoutSec * 1000);
        return () => {
            clearTimeout(t);
            clearInterval(ticker);
        };
    }, [customer, cart.length, clearTimeoutSec]);

    const performSearch = async (raw) => {
        const q = (raw || "").trim();
        if (!q) {
            toast.error("Εισάγετε κωδικό πελάτη");
            return;
        }
        setSearching(true);
        try {
            const mode = customerSearchMode(q);
            const url = `/customers/search/${encodeURIComponent(q)}${
                mode ? `?mode=${mode}` : ""
            }`;
            const { data } = await api.get(url);
            setCustomer(data);
            // Load favourites for this customer (top-20 distinct purchased items)
            api.get(`/customers/${data.id}/favourites?limit=20`)
                .then((r) => setFavourites(r.data))
                .catch(() => setFavourites([]));
            const remaining = data.remaining;
            const limitSet = data.period_limit_set !== false;
            const isVip = !!(data.is_vip || data.no_credit_control);
            const status = !limitSet
                ? "error"
                : remaining <= 0
                  ? "error"
                  : remaining <= 5
                    ? "warning"
                    : "success";
            setDisplay({
                status,
                title: !limitSet
                    ? "ΧΩΡΙΣ ΟΡΙΟ ΜΗΝΑ"
                    : isVip
                      ? "🛡️ VIP — ΧΩΡΙΣ ΕΛΕΓΧΟ"
                      : remaining <= 0
                        ? "ΧΩΡΙΣ CREDIT"
                        : remaining <= 5
                          ? "ΧΑΜΗΛΟ ΥΠΟΛΟΙΠΟ"
                          : "ΕΝΕΡΓΟΣ ΠΕΛΑΤΗΣ",
                primary: data.name,
                amount: limitSet
                    ? {
                          value: remaining,
                          label: isVip
                              ? "ΥΠΟΛΟΙΠΟ ΜΗΝΑ VIP"
                              : "ΥΠΟΛΟΙΠΟ ΜΗΝΑ",
                      }
                    : null,
                lines: !limitSet
                    ? [
                          `Κωδικός #${data.code}`,
                          "Δεν έχει οριστεί όριο μήνα — οι πωλήσεις είναι κλειδωμένες",
                      ]
                    : isVip
                      ? [
                            `Κωδικός #${data.code}`,
                            "Πλαφόν 5.000€/μήνα — μηδενίζει την 1η",
                        ]
                      : [
                            `Κωδικός #${data.code}`,
                            data.is_month_override
                                ? `🎯 ΕΙΔΙΚΟ ΟΡΙΟ ΜΗΝΑ ${fmtEUR(data.period_limit)}`
                                : `Όριο ${fmtEUR(data.period_limit ?? data.monthly_limit)}`,
                        ],
            });
            // Auto-focus item search when customer has credit (green status)
            if (limitSet && remaining > 0) {
                focusItemSearch();
            }
        } catch (err) {
            setCustomer(null);
            setDisplay({
                status: "error",
                title: "ΔΕΝ ΒΡΕΘΗΚΕ",
                primary: `#${q}`,
                lines: [err?.response?.data?.detail || "Δεν βρέθηκε πελάτης"],
            });
        } finally {
            setSearching(false);
        }
    };

    const addToCart = (item) => {
        if (!customer) {
            toast.error("Επιλέξτε πρώτα πελάτη");
            return;
        }
        if (customer.period_limit_set === false) {
            toast.error("Δεν έχει οριστεί μηνιαίο όριο πίστωσης για τον τρέχοντα μήνα");
            return;
        }
        // Credit cap: prevent adding if it would exceed remaining
        const currentLine = cart.find((l) => l.item.id === item.id);
        const currentQty = currentLine ? currentLine.quantity : 0;
        const otherGross = cart.reduce(
            (s, l) => (l.item.id === item.id ? s : s + l.item.unit_price * l.quantity),
            0
        );
        const newGross = otherGross + item.unit_price * (currentQty + 1);
        if (newGross > customer.remaining + 1e-9) {
            toast.error(
                `Υπέρβαση ορίου · Διαθέσιμο ${fmtEUR(customer.remaining)}`
            );
            return;
        }
        setCart((prev) => {
            const idx = prev.findIndex((l) => l.item.id === item.id);
            if (idx >= 0) {
                const nx = [...prev];
                nx[idx] = { ...nx[idx], quantity: nx[idx].quantity + 1 };
                return nx;
            }
            return [...prev, { item, quantity: 1 }];
        });
    };

    const handleScanned = async (text) => {
        const value = (text || "").trim();
        if (scannerMode === "customer") {
            setCode(value);
            performSearch(value);
        } else {
            if (!customer) {
                toast.error("Επιλέξτε πρώτα πελάτη");
                return;
            }
            // Place scanned value into the item search input first (visible feedback)
            setItemFilter(value);
            try {
                const { data } = await api.get(
                    `/items/search/${encodeURIComponent(value)}`
                );
                addToCart(data);
                toast.success(`Προστέθηκε: ${data.description}`);
                setItemFilter("");
                focusItemSearch();
            } catch {
                toast.error("Δεν βρέθηκε είδος με αυτό το barcode");
            }
        }
    };

    // Submit handler from item search box (Enter key) — looks up by code/barcode
    const handleItemSearchSubmit = async (q) => {
        if (!customer) {
            toast.error("Επιλέξτε πρώτα πελάτη");
            return false;
        }
        try {
            const { data } = await api.get(
                `/items/search/${encodeURIComponent(q)}`
            );
            addToCart(data);
            toast.success(`Προστέθηκε: ${data.description}`);
            focusItemSearch();
            return true;
        } catch {
            toast.error("Δεν βρέθηκε είδος");
            return false;
        }
    };

    const removeFromCart = (itemId) =>
        setCart((c) => c.filter((l) => l.item.id !== itemId));

    const updateQty = (itemId, delta) => {
        if (delta > 0 && customer) {
            // Credit cap check on increment
            const line = cart.find((l) => l.item.id === itemId);
            if (line) {
                const otherGross = cart.reduce(
                    (s, l) => (l.item.id === itemId ? s : s + l.item.unit_price * l.quantity),
                    0
                );
                const newGross = otherGross + line.item.unit_price * (line.quantity + delta);
                if (newGross > customer.remaining + 1e-9) {
                    toast.error(
                        `Υπέρβαση ορίου · Διαθέσιμο ${fmtEUR(customer.remaining)}`
                    );
                    return;
                }
            }
        }
        setCart((prev) =>
            prev
                .map((l) =>
                    l.item.id === itemId
                        ? { ...l, quantity: Math.max(0, l.quantity + delta) }
                        : l
                )
                .filter((l) => l.quantity > 0)
        );
    };

    const setQty = (itemId, value) => {
        const q = parseFloat(String(value).replace(",", "."));
        if (isNaN(q) || q <= 0) {
            removeFromCart(itemId);
            return;
        }
        if (customer) {
            const line = cart.find((l) => l.item.id === itemId);
            if (line) {
                const otherGross = cart.reduce(
                    (s, l) => (l.item.id === itemId ? s : s + l.item.unit_price * l.quantity),
                    0
                );
                const newGross = otherGross + line.item.unit_price * q;
                if (newGross > customer.remaining + 1e-9) {
                    toast.error(
                        `Υπέρβαση ορίου · Διαθέσιμο ${fmtEUR(customer.remaining)}`
                    );
                    return;
                }
            }
        }
        setCart((prev) =>
            prev.map((l) => (l.item.id === itemId ? { ...l, quantity: q } : l))
        );
    };

    // Numpad — global, writes into the currently active input (customer or item)
    const toggleNumpad = () => {
        if (numpad.isOpen) {
            numpad.close();
            return;
        }
        const isCustomer = activeTarget === "customer";
        const currentValue = isCustomer ? code : itemFilter;
        numpad.open({
            value: currentValue,
            label: isCustomer ? "Αναζήτηση πελάτη →" : "Αναζήτηση είδους →",
            onChange: (v) => {
                if (isCustomer) setCode(v);
                else setItemFilter(v);
            },
            onApply: (v) => {
                if (!v) return;
                if (isCustomer) {
                    performSearch(v);
                } else {
                    handleItemSearchSubmit(v).then((ok) => {
                        if (ok) setItemFilter("");
                    });
                }
            },
        });
        // Re-focus the active input
        setTimeout(() => {
            if (isCustomer) searchRef.current?.focus();
            else itemSearchRef.current?.focus();
        }, 50);
    };

    const totals = useMemo(() => computeTotals(cart), [cart]);

    const submitSale = async () => {
        if (!customer) {
            toast.error("Επιλέξτε πρώτα πελάτη");
            return;
        }
        if (cart.length === 0) {
            toast.error("Το καλάθι είναι κενό");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/sales", {
                customer_id: customer.id,
                line_items: cart.map((l) => ({
                    item_id: l.item.id,
                    quantity: l.quantity,
                })),
            });
            const overage = data.overage || 0;
            if (overage > 0) {
                setDisplay({
                    status: "warning",
                    title: "ΠΩΛΗΣΗ ΜΕ ΥΠΕΡΒΑΣΗ",
                    primary: `${data.customer_name} · #${data.customer_code}`,
                    amount: { value: 0, label: "ΝΕΟ ΥΠΟΛΟΙΠΟ" },
                    lines: [
                        `Σύνολο ${fmtEUR(data.total_gross)}`,
                        `Χρεώθηκαν ${fmtEUR(data.amount_charged)}`,
                        `ΕΠΙΠΛΕΟΝ ${fmtEUR(overage)}`,
                    ],
                });
            } else {
                setDisplay({
                    status: "success",
                    title: "ΕΓΚΡΙΘΗΚΕ",
                    primary: `${data.customer_name} · #${data.customer_code}`,
                    amount: {
                        value: customer.remaining - data.amount_charged,
                        label: "ΝΕΟ ΥΠΟΛΟΙΠΟ",
                    },
                    lines: [
                        `Σύνολο ${fmtEUR(data.total_gross)}`,
                        `${cart.length} είδη`,
                    ],
                });
            }
            setCart([]);
            setCustomer(null);
            setCode("");
            focusCustomerSearch();
            successOverlay.show();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα πώλησης");
        } finally {
            setSubmitting(false);
        }
    };

    const parkSale = async () => {
        if (cart.length === 0) {
            toast.error("Το καλάθι είναι κενό");
            return;
        }
        try {
            await api.post("/sales/park", {
                customer_id: customer?.id || null,
                line_items: cart.map((l) => ({
                    item_id: l.item.id,
                    quantity: l.quantity,
                })),
                note: "",
            });
            toast.success("Πώληση σε αναστολή");
            setCart([]);
            setCustomer(null);
            setCode("");
            setDisplay({
                status: "info",
                title: "ΣΕ ΑΝΑΣΤΟΛΗ",
                primary: "Η πώληση αποθηκεύτηκε",
                lines: ["Ανακτήστε τη από το κουμπί ΣΕ ΑΝΑΣΤΟΛΗ"],
            });
            loadParkedCount();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα αναστολής");
        }
    };

    const resumeParked = async (parked) => {
        const newCart = [];
        let missing = 0;
        for (const li of parked.line_items) {
            const it = items.find((i) => i.id === li.item_id);
            if (it) newCart.push({ item: it, quantity: li.quantity });
            else missing += 1;
        }
        setCart(newCart);
        if (parked.customer_id) {
            try {
                const { data } = await api.get(`/customers/${parked.customer_id}`);
                setCustomer(data);
                const remaining = data.remaining;
                const status =
                    remaining <= 0 ? "error" : remaining <= 5 ? "warning" : "info";
                setDisplay({
                    status,
                    title: "ΕΠΑΝΑΦΟΡΑ ΠΩΛΗΣΗΣ",
                    primary: data.name,
                    amount: { value: remaining, label: "ΥΠΟΛΟΙΠΟ ΜΗΝΑ" },
                    lines: [
                        `Κωδικός #${data.code}`,
                        `${newCart.length} είδη`,
                    ],
                });
            } catch {
                setDisplay({
                    status: "info",
                    title: "ΕΠΑΝΑΦΟΡΑ",
                    primary: parked.customer_name || "Πώληση",
                    lines: [`${newCart.length} είδη`],
                });
            }
        }
        if (missing > 0) {
            toast.warning(`${missing} είδη παραλείφθηκαν (διαγράφηκαν από τη βάση)`);
        } else {
            toast.success("Επαναφέρθηκε");
        }
        return true;
    };

    const handleClearCustomer = () => {
        setCustomer(null);
        setCart([]);
        setCode("");
        setItemFilter("");
        setFavourites([]);
        numpad.close();
        setDisplay(READY);
        focusCustomerSearch();
    };

    const handleClearRequest = () => {
        // Confirm only if there are items in the cart
        if (cart.length > 0) {
            setConfirmClearOpen(true);
        } else {
            handleClearCustomer();
        }
    };

    return (
        <div
            data-testid="dashboard-page"
            className="flex-1 flex flex-col min-h-0 bg-[#FAFAFA]"
        >
            {periodLimit && periodLimit.is_set === false && (
                <div
                    className="shrink-0 bg-[#FDEAE8] border-b-2 border-[#FF3B30] px-5 py-3 flex items-center gap-4"
                    data-testid="no-period-limit-banner"
                >
                    <Warning
                        size={28}
                        weight="fill"
                        className="text-[#FF3B30] shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#B11919]">
                            ΠΡΟΣΟΧΗ — ΕΝΑΡΞΗ ΜΗΝΑ
                        </div>
                        <div className="text-base font-bold text-[#0A0A0A]">
                            ΔΕΝ ΕΧΕΙ ΟΡΙΣΤΕΙ ΟΡΙΟ ΠΙΣΤΩΣΗΣ ΓΙΑ ΤΟΝ ΜΗΝΑ{" "}
                            <span className="text-[#B11919]">
                                {monthLabelGr(periodLimit.month_key)}
                            </span>
                            . Καμία πώληση δεν θα καταχωρηθεί μέχρι να το ορίσετε.
                        </div>
                    </div>
                    <button
                        onClick={() => navigate("/backup")}
                        data-testid="goto-set-limit-btn"
                        className={`shrink-0 bg-[#FF3B30] hover:bg-[#D72D24] text-white font-black tracking-wider px-5 py-3 flex items-center gap-2 transition-colors ${
                            canAccessSecurity ? "" : "hidden"
                        }`}
                    >
                        ΟΡΙΣΜΟΣ ΟΡΙΟΥ
                        <ArrowRight size={18} weight="bold" />
                    </button>
                </div>
            )}
            <DisplayPanel display={display} clearCountdown={clearCountdown} />

            {/* Customer search bar — shown only when no customer is selected */}
            {!customer && (
                <div className="shrink-0 px-3 py-2 bg-white border-b border-[#E2E4E9]">
                    <CustomerSearch
                        ref={searchRef}
                        customer={null}
                        code={code}
                        setCode={setCode}
                        searching={searching}
                        onSearch={performSearch}
                        onClear={handleClearCustomer}
                        onFocus={(k) => setActiveTarget(k)}
                        onOpenScanner={() => {
                            setScannerMode("customer");
                            setScannerOpen(true);
                        }}
                    />
                </div>
            )}

            {/* Two-column POS layout — cart wider (left), items grid smaller (right) */}
            <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 gap-2 p-2">
                <div className="lg:col-span-7 min-h-0">
                    <CartTable
                        cart={cart}
                        totals={totals}
                        onRemove={removeFromCart}
                        onUpdateQty={updateQty}
                        onSetQty={setQty}
                    />
                </div>
                <div className="lg:col-span-5 min-h-0">
                    <ItemGrid
                        ref={itemSearchRef}
                        items={items}
                        favourites={favourites}
                        filter={itemFilter}
                        setFilter={setItemFilter}
                        activeCategory={activeCategory}
                        setActiveCategory={setActiveCategory}
                        onAdd={(it) => {
                            addToCart(it);
                            focusItemSearch();
                        }}
                        onSearchSubmit={handleItemSearchSubmit}
                        onInputFocus={(k) => setActiveTarget(k)}
                        disabled={!customer}
                        onOpenScanner={() => {
                            // Use active input to decide scan target
                            setScannerMode(activeTarget === "customer" ? "customer" : "item");
                            setScannerOpen(true);
                        }}
                    />
                </div>
            </div>

            {/* Action bar */}
            <div className="shrink-0 px-2 pb-2">
                <PosActionBar
                    onClear={handleClearRequest}
                    onPark={parkSale}
                    onSubmit={submitSale}
                    onOpenCustomer={handleClearRequest}
                    onOpenParked={() => setParkedOpen(true)}
                    onOpenScanner={() => {
                        // Scan target follows active input
                        setScannerMode(activeTarget === "customer" ? "customer" : "item");
                        setScannerOpen(true);
                    }}
                    onOpenNumpad={toggleNumpad}
                    onOpenReturn={() => setReturnOpen(true)}
                    submitting={submitting}
                    canSubmit={
                        !!customer &&
                        cart.length > 0 &&
                        customer.remaining > 0 &&
                        customer.period_limit_set !== false
                    }
                    cartEmpty={cart.length === 0}
                    parkedCount={parkedCount}
                    hasCustomer={!!customer}
                    numpadOpen={numpad.isOpen}
                />
            </div>

            <BarcodeScanner
                open={scannerOpen}
                onClose={() => setScannerOpen(false)}
                onScan={handleScanned}
            />

            <ParkedSalesDialog
                open={parkedOpen}
                onClose={() => setParkedOpen(false)}
                onResume={resumeParked}
                onChange={loadParkedCount}
            />

            <ReturnDialog
                open={returnOpen}
                onClose={() => setReturnOpen(false)}
                onSuccess={() => {
                    // Reset to "select customer" state after a successful return.
                    setCustomer(null);
                    setCart([]);
                    setCode("");
                    setDisplay(READY);
                    focusCustomerSearch();
                }}
            />

            <AlertDialog open={confirmClearOpen} onOpenChange={setConfirmClearOpen}>
                <AlertDialogContent
                    className="rounded-none border-2 border-[#0A0A0A]"
                    data-testid="confirm-clear-dialog"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading text-2xl font-black tracking-tighter">
                            ΑΚΥΡΩΣΗ ΠΩΛΗΣΗΣ;
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-base">
                            Το καλάθι έχει {cart.length} είδη ({fmtEUR(totals.gross)}).
                            Θέλετε σίγουρα να ακυρωθεί η πώληση;
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="confirm-clear-cancel"
                            className="rounded-none border-2 border-[#0A0A0A] font-bold"
                        >
                            ΟΧΙ
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="confirm-clear-ok"
                            onClick={handleClearCustomer}
                            className="rounded-none bg-[#FF3B30] hover:bg-[#D72D24] text-white font-black"
                        >
                            ΝΑΙ, ΑΚΥΡΩΣΗ
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
